// GameTable.jsx - Complete file with proper seating phase view
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CardUtils from '../../../utils/CardUtils';
import PlayerHand from '../../EnhancedRummyGame/components/PlayerHand';
import { MicIcon, SpeakerIcon } from './VoiceIcons';
import './GameTable.css';

const GameTable = ({ 
  gameState, 
  jokerCard, 
  onDrawFromDeck, 
  onDrawFromDiscard, 
  onShowDiscardModal,
  seatingPhase = false,
  countdown = 0,
  seatingData = null,
  firstPlayerId = null,
  currentUserId = null,
  onSkipSeating,
  isMultiplayer = false,
  isPlayerTurn = false,
  isMobile = false,
  containerWidth = '100%',
  selectedCardsForGroup,
  onCardSelect,
  onAutoArrange,
  onCreateGroup,
  onResetGroupSelection,
  onDeclaration,
  onDiscard,
  onMoveCard,
  onDrop,
  hasDrawnThisTurn = false,
  isArrangeUsed = false,
  deckRef,
  discardRef,
  circleRefs,
    disabled = false,
  isSyncingAfterDraw = false,
        // Submission phase props (Simultaneous)
  isSubmissionPhase = false,
  hasSubmitted = false,
  submissionTimeLeft = null,
  onSubmitArrangement = null,
  isWaitingAsDeclarer = false,
  declarationNotification = '',
  voiceEnabled = false,
  voiceStates = {},
  dealerPlayerId = null,
  isWaiting = false,
  waitingRoomData = null,
  roomCountdown = null,
  isAutoDropQueued = false,
  onToggleAutoDrop = null,
  cashGameInfo = null,
  roomConfig = null,
  isRoomCreator = false,
  onStartGame = null,
  onDropBlocked = null,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [playerPositions, setPlayerPositions] = useState([]);
  const [showGroupBtn, setShowGroupBtn] = useState(false);
    const [showAllDiscards, setShowAllDiscards] = useState(false);

    const [remainingTime, setRemainingTime] = useState(null);
    const [isLandscapeMode, setIsLandscapeMode] = useState(false);

    // ---- Persistent-table phase helpers ----
    const [seatingStage, setSeatingStage] = useState('reveal'); // 'reveal' (cards drawn) -> 'arrange' (players glide to seats)
    const [isDealing, setIsDealing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [startingGame, setStartingGame] = useState(false);
    const [startError, setStartError] = useState('');
    const joinOrderRef = useRef([]);
    const prevSeatingRef = useRef(false);

    useEffect(() => {
      if (!seatingPhase) return undefined;
      setSeatingStage('reveal');
      setIsDealing(false);
      const t = setTimeout(() => setSeatingStage('arrange'), 3500);
      return () => clearTimeout(t);
    }, [seatingPhase]);

    // When seating ends, show a short "dealing cards" animation before the hand appears
    useEffect(() => {
      if (prevSeatingRef.current && !seatingPhase) {
        prevSeatingRef.current = false;
        setIsDealing(true);
        const t = setTimeout(() => setIsDealing(false), 3000);
        return () => clearTimeout(t);
      }
      prevSeatingRef.current = seatingPhase;
      return undefined;
    }, [seatingPhase]);

    // Detect landscape orientation on mobile
    useEffect(() => {
      const checkOrientation = () => {
        setIsLandscapeMode(isMobile && window.innerWidth > window.innerHeight);
      };
      checkOrientation();
      window.addEventListener('resize', checkOrientation);
      window.addEventListener('orientationchange', checkOrientation);
      return () => {
        window.removeEventListener('resize', checkOrientation);
        window.removeEventListener('orientationchange', checkOrientation);
      };
    }, [isMobile]);

  useEffect(() => {
    setShowGroupBtn(selectedCardsForGroup?.length >= 2);
  }, [selectedCardsForGroup]);

  // Server-clock offset: (server time) - (this device's time). A phone whose clock is wrong would otherwise
  // show a stuck / empty timer for the other players' turns, so every reading is corrected with this offset.
  // The offset must be measured ONCE per received state (when serverNow changes). Recomputing it on every
  // render would cancel out the passing time (serverNow stays fixed while Date.now() moves) and freeze the timer.
  const clockOffsetRef = useRef(0);
  const lastServerNowRef = useRef(null);
  if (gameState?.serverNow && gameState.serverNow !== lastServerNowRef.current) {
    lastServerNowRef.current = gameState.serverNow;
    clockOffsetRef.current = gameState.serverNow - Date.now();
  }

  // Timer effect
  useEffect(() => {
    if (!gameState?.turnStartTimestamp || !gameState?.turnTotalAllowed) {
      setRemainingTime(null);
      return;
    }
    const updateRemaining = () => {
      const now = Date.now() + clockOffsetRef.current;
      const elapsed = Math.max(0, now - gameState.turnStartTimestamp);
      const remaining = Math.max(0, Math.min(gameState.turnTotalAllowed, gameState.turnTotalAllowed - elapsed));
      setRemainingTime(remaining);
    };
    updateRemaining();
    const interval = setInterval(updateRemaining, 100);
    return () => clearInterval(interval);
  }, [gameState?.turnStartTimestamp, gameState?.turnTotalAllowed, gameState?.serverNow]);

  const positionCoordinates = {
    bottom: { top: '83%', left: '50%' },
    right: { top: '50%', left: '84%' },
    topRight: { top: '18%', left: '76%' },
    topCenter: { top: '15%', left: '50%' },
    topLeft: { top: '18%', left: '24%' },
    left: { top: '50%', left: '16%' }
  };

  // Player positioning logic
  useEffect(() => {
    // Collect players: prefer gameState.players, fall back to waitingRoomData.players
    let rawPlayers = (gameState?.players && gameState.players.length > 0)
      ? gameState.players
      : (waitingRoomData?.players || []);

    if (rawPlayers.length === 0 && currentUserId) {
      rawPlayers = [{ userId: currentUserId, username: 'YOU' }];
    }

    if (rawPlayers.length > 0 && currentUserId) {
      const players = [...rawPlayers];
      const sameId = (a, b) => a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
      const isSamePlayer = (p, id) => sameId(p.userId, id) || sameId(p.user_id, id) || sameId(p.id, id);

      let currentPlayer = players.find(p => isSamePlayer(p, currentUserId));
      if (!currentPlayer) {
        currentPlayer = { userId: currentUserId, username: 'YOU' };
        players.unshift(currentPlayer);
      }

      // Seating order = players ranked by their seating-phase spade card (A high … 2 low).
      const rankValue = (rank) => ({ '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13, A: 14 }[String(rank)] || 0);
      const rankedFromCards = (seatingData?.seatingCards?.length
        ? [...seatingData.seatingCards]
            .sort((a, b) => rankValue(b.rank) - rankValue(a.rank))
            .map(c => c.userId)
        : null);
      const joinIds = players.map(p => p.userId || p.user_id || p.id);
      const revealingSeats = seatingPhase && seatingStage === 'reveal';
      if (!seatingPhase) joinOrderRef.current = joinIds;
      const candidateOrder = revealingSeats
        ? (joinOrderRef.current.length ? joinOrderRef.current : joinIds)
        : (seatingPhase ? seatingData?.seatingOrder : null)
        || gameState?.playerOrder
        || seatingData?.seatingOrder
        || rankedFromCards
        || players.map(p => p.userId || p.user_id || p.id);

      // Keep only players that are actually at the table, then append anyone missing
      const seatingOrder = candidateOrder.filter(id => players.some(p => isSamePlayer(p, id)));
      players.forEach(p => {
        const id = p.userId || p.user_id || p.id;
        if (!seatingOrder.some(existing => sameId(existing, id))) seatingOrder.push(id);
      });

      const currentPlayerIndex = seatingOrder.findIndex(id => isSamePlayer(currentPlayer, id));
      if (currentPlayerIndex === -1) return;
      const seatedCount = seatingOrder.length;

      // Current player is ALWAYS at the bottom; the rest follow clockwise in seating order
      const reorderedPlayers = [{ ...currentPlayer, position: 'bottom', isCurrentPlayer: true }];
      for (let i = 1; i < seatedCount; i++) {
        const nextPlayerId = seatingOrder[(currentPlayerIndex + i) % seatedCount];
        const nextPlayer = players.find(p => isSamePlayer(p, nextPlayerId));
        if (nextPlayer) reorderedPlayers.push({ ...nextPlayer, isCurrentPlayer: false });
      }

      const positionMapping = {
        2: ['bottom', 'topCenter'],
        3: ['bottom', 'right', 'topCenter'],
        4: ['bottom', 'right', 'topCenter', 'left'],
        5: ['bottom', 'right', 'topRight', 'topCenter', 'left'],
        6: ['bottom', 'right', 'topRight', 'topCenter', 'topLeft', 'left']
      };
      const positions = positionMapping[reorderedPlayers.length] || positionMapping[6];

      const positionedPlayers = reorderedPlayers.map((player, index) => {
        const positionKey = positions[index];
        const pId = player.userId || player.user_id || player.id;
        return {
          ...player,
          position: positionKey,
          positionCoords: positionCoordinates[positionKey],
          isCurrentTurn: !isWaiting && (gameState?.currentTurnPlayerId === player.userId || gameState?.currentTurnPlayerId === player.id),
          isFirstPlayer: pId === firstPlayerId,
          // Dealer is only known AFTER the seating cards decide the order: nobody is dealer while
          // waiting or while the cards are still being drawn; then it is the last seat in the order.
          isDealer: (() => {
            if (isWaiting || gameState?.status === 'waiting') return false;
            if (seatingPhase) {
              if (seatingStage === 'reveal') return false;
              const lastId = seatingOrder[seatingOrder.length - 1];
              return String(pId) === String(lastId);
            }
            return String(pId) === String(dealerPlayerId);
          })()
        };
      });
      setPlayerPositions(positionedPlayers);
    }
  }, [gameState, currentUserId, seatingData, firstPlayerId, seatingPhase, seatingStage, waitingRoomData, isWaiting, dealerPlayerId]);

  // Helper to get player's seating card
  const getPlayerSeatingCard = (playerId) => {
    if (!seatingData?.seatingCards) return null;
    return seatingData.seatingCards.find(c => String(c.userId) === String(playerId)) || null;
  };

  // Render player circles with optional seating card info
  const renderPlayerCircles = () => {
    if (!playerPositions.length) return null;

    return playerPositions.map((player, index) => {
      const isFirstPlayer = player.userId === firstPlayerId || player.id === firstPlayerId;
      const firstName = player.username?.split(' ')[0] || 'Player';
      const displayName = firstName.length > 6 ? firstName.substring(0, 6) + '..' : firstName;
      const circleSize = isMobile ? '70px' : '85px';
      const seatingCard = seatingPhase ? getPlayerSeatingCard(player.userId || player.id) : null;

      let timerClass = '';
      let countdownValue = null;
      let countdownBlink = false;
      let timerProgress = 1;
      let timerStrokeColor = '#00e676';

      if (!seatingPhase && !isWaiting && player.isCurrentTurn && remainingTime !== null && gameState?.turnTotalAllowed) {
        const totalAllowed = gameState.turnTotalAllowed;
        const elapsed = totalAllowed - remainingTime;
        const normalDuration = 30000;
        if (elapsed <= normalDuration) {
          const remainingNormal = normalDuration - elapsed;
          countdownValue = Math.ceil(remainingNormal / 1000);
          timerProgress = Math.max(0, remainingNormal / normalDuration);
          if (remainingNormal <= 7000) {
            countdownBlink = true;
            timerClass = 'timer-blink';
            timerStrokeColor = '#ff1744';
          } else if (remainingNormal <= 15000) {
            timerClass = 'timer-yellow';
            timerStrokeColor = '#ffab00';
          } else {
            timerClass = 'timer-green';
            timerStrokeColor = '#00e676';
          }
        } else {
          const bonusElapsed = elapsed - normalDuration;
          const bonusRemaining = player.bonusRemaining || 0;
          const estimatedBonusRemaining = Math.max(0, bonusRemaining - bonusElapsed);
          countdownValue = Math.ceil(estimatedBonusRemaining / 1000);
          timerProgress = Math.max(0, estimatedBonusRemaining / 15000);
          timerStrokeColor = '#7c4dff';
          if (estimatedBonusRemaining < 5000) countdownBlink = true;
          const turnRemaining = totalAllowed - elapsed;
          timerClass = turnRemaining < 5000 ? 'timer-blink' : 'timer-bonus';
        }
      }

      // Light-blue circle so every player is easy to spot; on a player's turn the circle itself becomes
      // the CLOCK FACE (hand sweeps round, colour goes green -> yellow -> red).
      const turnActive = !seatingPhase && !isWaiting && player.isCurrentTurn && countdownValue !== null;
      const clockFace = timerStrokeColor === '#ff1744' ? 'rgba(255,23,68,0.28)'
        : timerStrokeColor === '#ffab00' ? 'rgba(255,171,0,0.28)'
        : timerStrokeColor === '#7c4dff' ? 'rgba(124,77,255,0.30)'
        : 'rgba(0,230,118,0.26)';
      const circleBgColor = turnActive ? clockFace : 'rgba(100, 181, 246, 0.55)';
      let textColor = '#ffffff';
      if (player.isCurrentPlayer) textColor = '#e8ffe8';

      let circleTop = player.positionCoords.top;
      let circleLeft = player.positionCoords.left;
      let circleBottom = 'auto';
      let circleTransform = 'translate(-50%, -50%)';
      let circlePosition = 'absolute';
      // Keep the current player's circle centered above their hand, in the gap
      // between the hand and the deck/discard pile, so the avatar, name, score
      // and timer never cover the cards.
      if (player.isCurrentPlayer && !seatingPhase) {
        // Current player's circle now lives on the LEFT side of the table (beside the side bar),
        // leaving the centre free. It blinks together with the timer when it is your turn.
        circleTop = isMobile ? '55%' : '58%';
        circleLeft = isMobile ? '84px' : '100px';
        circleTransform = 'translate(-50%, -50%)';
      }
      // Seating, step 1: everybody gathers in the CENTRE of the table - circles on top, their cards below -
      // then (step 2, seatingStage 'arrange') they glide to their seats according to the card value.
      if (seatingPhase && seatingStage === 'reveal') {
        const n = playerPositions.length;
        const spacing = Math.min(isMobile ? 15 : 13, 62 / Math.max(1, n));
        circleTop = '40%';
        circleLeft = `${50 + (index - (n - 1) / 2) * spacing}%`;
      }


      return (
        <div
          key={player.userId || player.id || index}
          className={`player-circle ${player.position} ${player.isCurrentPlayer ? 'current-player' : 'opponent'} ${isFirstPlayer ? 'first-player' : ''} ${player.isCurrentTurn ? 'current-turn' : ''}`}
          ref={el => {
            if (el) circleRefs.current[player.userId || player.id] = el;
            else delete circleRefs.current[player.userId || player.id];
          }}
          style={{
            position: circlePosition,
            top: circleTop,
            left: circleLeft,
            bottom: circleBottom,
            transform: circleTransform,
            transition: 'top 1.2s cubic-bezier(0.25, 1, 0.5, 1), left 1.2s cubic-bezier(0.25, 1, 0.5, 1), transform 0.6s ease',
            width: circleSize,
            height: circleSize,
            borderRadius: '50%',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            overflow: 'visible',
            boxSizing: 'border-box',
            border: '3px solid rgba(255,255,255,0.75)',
            backgroundColor: circleBgColor,
            boxShadow: '0 2px 10px rgba(0,0,0,0.55)',
          }}
        >
          {/* ⏱️ CLOCK TIMER: the circle is the clock face. A hand sweeps round like a clock's second hand
              (12 o'clock -> full turn over the turn time), the elapsed part fills in, colour = green/yellow/red. */}
          {turnActive && (
            <svg
              className="player-turn-svg-ring"
              viewBox="0 0 100 100"
              style={{
                position: 'absolute', top: -3, left: -3, width: 'calc(100% + 6px)', height: 'calc(100% + 6px)',
                pointerEvents: 'none', overflow: 'visible', zIndex: 101, borderRadius: '50%'
              }}
            >
              {/* remaining-time rim */}
              <circle
                cx="50" cy="50" r="47" fill="none" pathLength="100" transform="rotate(-90 50 50)"
                stroke={timerStrokeColor} strokeWidth="6" strokeLinecap="round"
                strokeDasharray="100" strokeDashoffset={100 * (1 - Math.max(0, Math.min(1, timerProgress)))}
                style={{ transition: 'stroke-dashoffset 0.12s linear, stroke 0.3s ease' }}
              />
              {/* tick marks like a clock */}
              {[0, 90, 180, 270].map((a) => (
                <line key={a} x1="50" y1="6" x2="50" y2="14" stroke="rgba(255,255,255,0.85)" strokeWidth="2"
                  transform={`rotate(${a} 50 50)`} />
              ))}
              {/* the sweeping second hand */}
              <g transform={`rotate(${(1 - Math.max(0, Math.min(1, timerProgress))) * 360} 50 50)`}
                 style={{ transition: 'transform 0.12s linear', filter: countdownBlink ? 'drop-shadow(0 0 5px #ff1744)' : 'drop-shadow(0 0 2px rgba(0,0,0,0.8))' }}>
                <line x1="50" y1="50" x2="50" y2="8" stroke={timerStrokeColor} strokeWidth="3.5" strokeLinecap="round" />
                <circle cx="50" cy="50" r="4.5" fill={timerStrokeColor} stroke="#fff" strokeWidth="1.5" />
              </g>
            </svg>
          )}

          {/* 🏷️ DEALER "D" BADGE */}
          {player.isDealer && (
            <div
              className="dealer-badge"
              title="Dealer (Round ends here)"
              style={{
                position: 'absolute', top: '-6px', right: '-6px',
                width: isMobile ? '22px' : '26px', height: isMobile ? '22px' : '26px',
                borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 35%, #ffffff 0%, #e0e0e0 55%, #9e9e9e 100%)',
                border: '2px solid #FFD700', color: '#111', fontWeight: '900',
                fontSize: isMobile ? '11px' : '13px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 106,
                boxShadow: '0 2px 6px rgba(0,0,0,0.8), inset 0 1px 1px #fff'
              }}
            >
              D
            </div>
          )}

          {!seatingPhase && !isWaiting && countdownValue !== null && (
            <div data-tour="timer" className={`player-countdown ${isMobile ? 'mobile' : ''} ${countdownBlink ? 'blink-text' : ''}`}>
              {countdownValue}s
            </div>
          )}
          <div className="player-name-display" style={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: isMobile ? '8px' : '12px', color: textColor, fontWeight: player.isCurrentTurn ? 'bold' : 'normal',
            textShadow: player.isCurrentTurn ? '0 0 2px white, 0 0 4px rgba(255,255,255,0.5)' : '2px 2px 6px rgba(0,0,0,0.9)',
            textAlign: 'center', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative', zIndex: 102
          }}>
            {displayName}
          </div>
          {!seatingPhase && voiceEnabled && !player.isCurrentPlayer && (() => {
            // Opponent's mic / speaker state, shown next to their circle
            const vs = voiceStates?.[String(player.userId || player.id)] || { micEnabled: false, speakerEnabled: true };
            const iconSize = isMobile ? 11 : 13;
            return (
              <div className="player-voice-badges" style={{
                position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
                display: 'flex', gap: '4px', alignItems: 'center', background: 'rgba(0,0,0,0.7)',
                borderRadius: '10px', padding: '2px 6px', zIndex: 103, pointerEvents: 'none'
              }}>
                <MicIcon off={!vs.micEnabled} size={iconSize} />
                <SpeakerIcon off={vs.speakerEnabled === false} size={iconSize} />
              </div>
            );
          })()}
          {seatingPhase && seatingCard && (
            // Small "place card" container with the spade inside; it is a child of the circle so both move together.
            <div className="seating-card-wrap" style={{
              position: 'absolute', ...seatCardPos(seatingStage === 'reveal' ? 'topCenter' : player.position),
              width: isMobile ? 28 : 36, height: isMobile ? 40 : 50, boxSizing: 'border-box',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: '#fff', border: '2px solid #FFD700', borderRadius: 6, zIndex: 1200, pointerEvents: 'none',
              boxShadow: '0 3px 10px rgba(0,0,0,0.8)', color: '#111', fontWeight: 900, lineHeight: 1,
              animation: 'seatCardIn 0.6s ease-out both', animationDelay: `${index * 0.35}s`
            }}>
              <span style={{ fontSize: isMobile ? 13 : 17 }}>{seatingCard.rank}</span>
              <span style={{ fontSize: isMobile ? 12 : 16 }}>♠</span>
            </div>
          )}
                    <div className={`player-score ${player.isCurrentPlayer ? 'current-player-score' : 'opponent-score'}`} style={{
            position: 'absolute', bottom: '-4px', color: player.isCurrentPlayer ? '#FFC107' : '#2196F3',
            borderRadius: '12px', fontSize: isMobile ? '11px' : '13px', fontWeight: 'bold', whiteSpace: 'nowrap',
            minWidth: '40px', textAlign: 'center', backgroundColor: player.isCurrentTurn ? 'rgba(0,0,0,0.5)' : 'transparent', padding: '2px 4px'
          }}>
            {player.score || 0}
          </div>
          {/* Show cumulative score below the per-deal score */}
          {player.cumulativeScore !== undefined && player.cumulativeScore !== null && (
            <div className={`player-cumulative-score`} style={{
              position: 'absolute', bottom: '-22px', color: '#FF6B6B',
              borderRadius: '8px', fontSize: isMobile ? '8px' : '10px', fontWeight: 'bold', whiteSpace: 'nowrap',
              minWidth: '40px', textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '1px 4px',
              border: '1px solid rgba(255,107,107,0.3)'
            }}>
              Cum: {player.cumulativeScore || 0}
            </div>
          )}
        </div>
      );
    });
  };

  const renderEmptyCircles = () => {
    if (!playerPositions.length) return null;
    const totalPlayers = playerPositions.length;
    const maxPlayers = 6;
    const emptyCount = maxPlayers - totalPlayers;
    if (emptyCount <= 0) return null;
    
    const allOpponentPositions = ['topCenter', 'left', 'right', 'topLeft', 'topRight'];
    const occupiedOpponentPositions = playerPositions.filter(p => !p.isCurrentPlayer).map(p => p.position);
    const emptyPositions = allOpponentPositions.filter(pos => !occupiedOpponentPositions.includes(pos)).slice(0, emptyCount);
    const circleSize = isMobile ? '55px' : '70px';
    
    return emptyPositions.map((pos, index) => (
      <div
        key={`empty-${index}`}
        className="player-circle empty-circle"
        style={{
          position: 'absolute',
          top: positionCoordinates[pos].top,
          left: positionCoordinates[pos].left,
          transform: 'translate(-50%, -50%)',
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          zIndex: 99,
          opacity: 0.6,
          backgroundColor: 'rgba(255,255,255,0.1)',
        }}
      >
        <div className="empty-circle-label">empty</div>
      </div>
    ));
  };

  const hudTextShadow = '0 2px 8px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.9)';

  // Seating card sits IN FRONT of the player (towards the table centre). It is a child of the player
  // circle, so it glides together with the circle when the players are re-arranged.
  const seatCardPos = (pos) => {
    const w = isMobile ? 28 : 36;
    const h = isMobile ? 40 : 50;
    // bottom: just above the circle | right seat: left of the circle | left seat: right of the circle
    // top-right / top-center / top-left: below the circle
    if (pos === 'bottom') return { left: '50%', marginLeft: -w / 2, bottom: 'calc(100% + 6px)' };
    if (pos === 'left') return { top: '50%', marginTop: -h / 2, left: 'calc(100% + 8px)' };
    if (pos === 'right') return { top: '50%', marginTop: -h / 2, right: 'calc(100% + 8px)' };
    return { left: '50%', marginLeft: -w / 2, top: 'calc(100% + 6px)' };
  };

  const renderSeatingPhaseOverlay = () => {
    if (!seatingPhase || !seatingData) return null;
    const firstId = seatingData.firstPlayerId || seatingData.firstPlayer || firstPlayerId;
    const firstPlayerObj = playerPositions.find(p => String(p.userId || p.user_id || p.id) === String(firstId));
    const tossWinnerName = firstPlayerObj?.username || 'Player';
    const countdownValue = countdown || seatingData.countdown || 0;

    return (
      <div className="table-seating-banner" style={{
        position: 'absolute', top: '48%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 101, display: 'flex', flexDirection: 'column', alignItems: 'center',
        background: 'transparent', textAlign: 'center', pointerEvents: 'none', textShadow: hudTextShadow
      }}>
        <div style={{ fontSize: isMobile ? '18px' : '24px', color: '#4CAF50', fontWeight: 'bold' }}>
          🎯 Seating
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '15px', color: '#fff', margin: '4px 0', fontWeight: 600 }}>
          {seatingStage === 'reveal' ? 'Drawing seating cards...' : `♠ ${tossWinnerName} plays first`}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#FFC107', fontWeight: 'bold' }}>
          Cards dealing in {countdownValue}s
        </div>
      </div>
    );
  };

  const copyRoomCode = () => {
    const text = waitingRoomData?.roomCode || roomConfig?.roomCode;
    if (!text) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
      done();
    }
  };

  const handleStartClick = async () => {
    if (!onStartGame || startingGame) return;
    setStartingGame(true);
    setStartError('');
    try {
      const res = await onStartGame();
      if (res && res.success === false) setStartError(res.error || 'Failed to start game');
    } finally {
      setStartingGame(false);
    }
  };

  const renderDealingLayer = () => {
    if (!isDealing || !playerPositions.length) return null;
    const n = playerPositions.length;
    const cardsEach = 13;
    const step = 2.0 / (cardsEach * n);
    const w = isMobile ? 26 : 34;
    const h = isMobile ? 38 : 48;
    const cards = [];
    for (let r = 0; r < cardsEach; r++) {
      playerPositions.forEach((p, pi) => {
        cards.push(
          <div
            key={`deal-${r}-${pi}`}
            className="deal-fly-card"
            style={{ width: w, height: h, '--dx': p.positionCoords.left, '--dy': p.positionCoords.top, animationDelay: `${(r * n + pi) * step}s` }}
          />
        );
      });
    }
    return (
      <div className="dealing-layer" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 90 }}>
        {cards}
      </div>
    );
  };

  const renderWaitingHUD = () => {
    if (!isWaiting || seatingPhase) return null;
    const isCustom = !!roomConfig?.isCustomRoom;
    const isCash = !isCustom && (!!cashGameInfo || !!roomConfig?.isCashGame);
    const roomCode = waitingRoomData?.roomCode || roomConfig?.roomCode;
    const currentCount = waitingRoomData?.playerCount || playerPositions.length || 1;
    const maxP = waitingRoomData?.maxPlayers || roomConfig?.maxPlayers || 6;
    const hasTimer = !isCustom && roomCountdown !== null && roomCountdown !== undefined;
    const secsTotal = Math.max(0, Number(roomCountdown) || 0);
    const timeFormatted = `${Math.floor(secsTotal / 60).toString().padStart(2, '0')}:${(secsTotal % 60).toString().padStart(2, '0')}`;
    const currency = cashGameInfo?.currency || roomConfig?.currency || 'USD';
    const stake = cashGameInfo?.entryFee || roomConfig?.entryFee || roomConfig?.amount || 0;
    const winAmt = cashGameInfo?.winningAmount || roomConfig?.winningAmount || (stake * maxP * 0.90);
    const canStart = isCustom && isRoomCreator && currentCount >= 2 && !startingGame;

    let statusText;
    if (isCustom) {
      statusText = isRoomCreator
        ? (currentCount >= 2 ? 'Press Start when everyone has joined' : 'Share the room code and wait for players...')
        : 'Waiting for the host to start the game...';
    } else if (currentCount >= maxP) {
      statusText = 'Table full - starting...';
    } else if (currentCount >= 2) {
      statusText = hasTimer ? `Game starts in ${timeFormatted}` : 'Starting soon...';
    } else {
      statusText = `Waiting for ${maxP - currentCount} more player(s)...`;
    }

    const chip = { color: '#fff', fontSize: isMobile ? '12px' : '14px', background: 'rgba(0,0,0,0.35)', padding: '3px 10px', borderRadius: '6px', fontFamily: 'monospace', letterSpacing: '1px' };
    const btn = { background: 'rgba(255,215,0,0.15)', border: '1px solid #FFD700', color: '#FFD700', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', pointerEvents: 'auto' };

    return (
      <div className="table-waiting-hud" style={{
        position: 'absolute', top: '48%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 100, display: 'flex', flexDirection: 'column', alignItems: 'center',
        background: 'transparent', maxWidth: '90%', textAlign: 'center', pointerEvents: 'none', textShadow: hudTextShadow
      }}>
        <h3 style={{ margin: '0 0 6px 0', color: '#FFD700', fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}>
          {isCash ? '💰 Cash Rummy' : (isCustom ? '🎪 Custom Room' : '♠️ Free Play Rummy')}
        </h3>

        {/* Room code + Copy only for custom rooms (cash / free play rooms are auto-matched) */}
        {roomCode && isCustom && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={chip}>{roomCode}</span>
            <button style={btn} onClick={copyRoomCode}>{copied ? '✓ Copied' : '📋 Copy'}</button>
          </div>
        )}

        {(isCash || isCustom) && stake > 0 && (
          <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: isMobile ? '11px' : '13px' }}>
            <span style={{ color: '#ddd' }}>Stake: <strong style={{ color: '#fff' }}>{currency} {Number(stake).toFixed(2)}</strong></span>
            {isCash && <span style={{ color: '#4CAF50' }}>Prize: <strong>{currency} {Number(winAmt).toFixed(2)}</strong></span>}
          </div>
        )}

        <div style={{ color: '#fff', fontSize: isMobile ? '13px' : '15px', marginBottom: '6px', fontWeight: 600 }}>
          👥 <span style={{ color: '#00e676' }}>{currentCount}</span> / {maxP}
        </div>

        <div style={{ color: '#e0e0e0', fontSize: isMobile ? '12px' : '14px', marginBottom: isCustom && isRoomCreator ? '10px' : 0 }}>
          {statusText}
        </div>

        {isCustom && isRoomCreator && (
          <button
            onClick={handleStartClick}
            disabled={!canStart}
            style={{ ...btn, padding: '8px 22px', fontSize: isMobile ? '13px' : '15px', fontWeight: 'bold', opacity: canStart ? 1 : 0.5, cursor: canStart ? 'pointer' : 'not-allowed' }}
          >
            {startingGame ? 'Starting...' : '🚀 Start Game'}
          </button>
        )}
        {startError && <div style={{ color: '#ff8a80', fontSize: '12px', marginTop: '6px' }}>{startError}</div>}
      </div>
    );
  };

  const renderCurrentPlayerHand = () => {
    if (seatingPhase) return null;
    if (isDealing) return null;
    if (!gameState?.players || !currentUserId) return null;
    const currentPlayer = gameState.players.find(p => p.userId === currentUserId || p.id === currentUserId);
    if (!currentPlayer) return null;
    const hasCards = currentPlayer.cards && currentPlayer.cards.length > 0;
    if (!hasCards) {
      return (
        <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px', borderRadius: '5px', zIndex: 1000 }}>
          Waiting for cards...
        </div>
      );
    }
    const hasGroups = currentPlayer.cards.some(card => card.isGroup);
    const cardDimensions = getCardDimensions();
    // Discard / Declare appear to the RIGHT of the hand only once the player has drawn and
    // selected exactly one card (the card to discard / declare with).
    const showHandActions = !seatingPhase && !isSubmissionPhase && gameState?.status === 'playing' &&
      isPlayerTurn && hasDrawnThisTurn && !isSyncingAfterDraw && selectedCardsForGroup?.length === 1;
    // Same look and size as the Arrange / Group / Drop buttons (.hud-action); only mobile needs the compact override
    const handActionBtn = () => (isMobile ? { minWidth: '68px', padding: '4px 6px', fontSize: '10px' } : {});
    // Hand geometry (shared by the hand and the buttons so the buttons always sit just ABOVE the hand)
    const handBottomPx = isLandscapeMode ? 14 : (isMobile ? 58 : 120);
    const handHeightPx = isLandscapeMode ? 100 : (isMobile ? 140 : 150);
    return (
      <>
                                                                                                {showHandActions && (
        <div className="hand-side-actions" style={{
          position: isMobile ? 'fixed' : 'absolute',
          // OUTSIDE the hand: directly above its top edge, on the right, just left of the Arrange/Group/Drop column
          right: isMobile ? '104px' : '122px',
          bottom: `${handBottomPx + handHeightPx + 2}px`,
          zIndex: 60, display: 'flex', flexDirection: 'row', gap: '8px', pointerEvents: 'auto'
        }}>
          <button className="hud-action declare" onClick={onDeclaration}
            title="Declare: selected card is discarded, remaining cards validated as groups"
            style={handActionBtn()}>
            Declare
          </button>
          <button className="hud-action discard" onClick={onDiscard}
            title="Discard selected card"
            style={handActionBtn()}>
            Discard
          </button>
        </div>
      )}
      <div className="current-player-hand-container" style={{
                                                  boxSizing: 'border-box',
                                                  paddingRight: 0,
                                                  position: isMobile ? 'fixed' : 'absolute',
                                                  bottom: `${handBottomPx}px`, left: '50%', transform: 'translateX(-50%)',
                                                                                                    width: '100%', maxWidth: '100%',                           height: `${handHeightPx}px`, zIndex: 50,
                                                  display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
                                                  background: 'transparent',
                                                  paddingBottom: isLandscapeMode ? '0px' : (isMobile ? 'env(safe-area-inset-bottom, 6px)' : '8px'),
                                                  paddingTop: isLandscapeMode ? '0px' : (isMobile ? '2px' : '0px'),
                                                  pointerEvents: 'auto',
                                                  overflowX: 'auto',
                                                  overflowY: 'hidden',
                                                }}>
        <PlayerHand
          player={currentPlayer}
          selectedCardsForGroup={selectedCardsForGroup}
          onCardSelect={onCardSelect}
          wildJokerCard={gameState?.wildJoker}
          isPlayerTurn={isPlayerTurn}
          isMultiplayer={true}
          isMobile={isMobile}
          dynamicSizes={{ cardSize: { width: parseInt(cardDimensions.discardCard.width), height: parseInt(cardDimensions.discardCard.height) } }}
          onAutoArrange={onAutoArrange}
          onCreateGroup={onCreateGroup}
          onResetGroupSelection={onResetGroupSelection}
          onDeclaration={onDeclaration}
          onDiscard={onDiscard}
          onMoveCard={onMoveCard}
        />
      </div>
      </>
    );
  };

                const renderActionButtons = () => {
    if (seatingPhase) return null;
    if (!gameState?.players || !currentUserId) return null;
    
    // ========== SIMULTANEOUS SUBMISSION PHASE ==========
    if (gameState?.status === 'submission' && isSubmissionPhase) {
      const currentPlayer = gameState.players.find(p => p.userId === currentUserId || p.id === currentUserId);
      
      // If no cards left (already submitted), show waiting message
      if (!currentPlayer || !currentPlayer.cards || currentPlayer.cards.length === 0 || hasSubmitted) {
        return (
          <div className="submission-status-message" style={{
              position: 'absolute', bottom: isLandscapeMode ? 'auto' : (isMobile ? '180px' : '175px'), top: isLandscapeMode ? '40px' : 'auto', left: '50%', transform: 'translateX(-50%)',
            background: 'rgba(33, 150, 243, 0.2)', padding: '10px 20px', borderRadius: '8px',
            border: '2px solid #4CAF50', textAlign: 'center', zIndex: 1100,
            maxWidth: '80%'
          }}>
            <div style={{ color: '#64B5F6', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>
              ✅ Submitted! Waiting for other players...
            </div>
            {submissionTimeLeft !== null && (
              <div style={{ color: '#90CAF9', fontSize: isMobile ? '10px' : '12px', marginTop: '4px' }}>
                ⏱ Phase ends in {Math.ceil(submissionTimeLeft / 1000)}s
              </div>
            )}
          </div>
        );
      }
      
      // Show submit button for ALL players simultaneously
      const hasUngrouped = (currentPlayer.cards || []).some(c => !c.isGroup);
      
      return (
                <div className="action-buttons-panel" style={{
                    position: 'absolute', bottom: isLandscapeMode ? 'auto' : (isMobile ? '145px' : '185px'), top: isLandscapeMode ? '40px' : 'auto', right: '20px', display: 'flex', flexDirection: 'column',
                              gap: '5px', padding: '8px', background: 'rgba(0, 0, 0, 0.7)', borderRadius: '8px', border: '1px solid #FF9800',
          zIndex: 1100, minWidth: '100px'
        }}>
          {/* Submission Timer */}
          {submissionTimeLeft !== null && (
            <div style={{
              textAlign: 'center',
              color: submissionTimeLeft < 10 ? '#f44336' : '#FFC107',
              fontSize: isMobile ? '11px' : '14px',
              fontWeight: 'bold',
              padding: '2px 4px',
              marginBottom: '4px',
              animation: submissionTimeLeft < 10 ? 'blink 1s infinite' : 'none',
            }}>
              ⏱ {Math.ceil(submissionTimeLeft / 1000)}s remaining
            </div>
          )}
                    {selectedCardsForGroup && selectedCardsForGroup.length >= 2 && (
            <button className="action-btn group-btn" onClick={onCreateGroup} title={`Create Group (${selectedCardsForGroup?.length || 0} selected)`}
              style={{ background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: 'pointer', width: '100%' }}>
              Group ({selectedCardsForGroup?.length})
            </button>
          )}
          {selectedCardsForGroup && selectedCardsForGroup.length > 0 && (
            <button className="action-btn clear-btn" onClick={onResetGroupSelection} title="Clear Selection"
              style={{ background: '#607D8B', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: 'pointer', width: '100%' }}>
              Clear
            </button>
          )}
          <button className="action-btn submit-btn" onClick={onSubmitArrangement} 
            title="Submit your card arrangement"
            style={{
              background: '#4CAF50',
              color: 'white', border: 'none', borderRadius: '4px', padding: '8px 12px',
              fontSize: isMobile ? '10px' : '12px', fontWeight: 'bold',
              cursor: 'pointer', width: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              animation: 'pulse-submit 2s ease-in-out infinite',
            }}>
            Submit Groups
          </button>
        </div>
      );
    }
    
    if (gameState?.status === 'finished') {
      return null;
    }

    const currentPlayer = gameState.players.find(p => p.userId === currentUserId || p.id === currentUserId);
    if (!currentPlayer || !currentPlayer.cards || currentPlayer.cards.length === 0) return null;

    const exactlyOneSelected = selectedCardsForGroup?.length === 1;
    const atLeastTwoSelected = selectedCardsForGroup?.length >= 2;

        // Declare requires exactly 1 card selected (the declared discard) and all other cards must be grouped
    const canDeclare = isPlayerTurn && hasDrawnThisTurn && exactlyOneSelected && !isSyncingAfterDraw;
        // Discard also requires exactly 1 card selected
    const canDiscard = isPlayerTurn && hasDrawnThisTurn && !isSyncingAfterDraw;
        // ?? POINTS RUMMY: Cannot drop if cumulative + penalty (25 first drop, 40 middle drop) would exceed maxPoints (mandatory play)
    const currentPlayerStats = gameState?.players?.find(p => p.userId === currentUserId || p.id === currentUserId);
    const hasDrawnAnyInDeal = currentPlayerStats?.hasDrawnAnyCard === true;
    // Pool rummy: first drop is 20 in the 101 game, 25 in 201 / 301 (middle drop is always 40)
    const firstDropPenalty = Number(gameState?.maxPoints) === 101 ? 20 : 25;
    const dropPenalty = hasDrawnAnyInDeal ? 40 : firstDropPenalty;
    const wouldExceedMaxPoints = gameState?.gameMode === 'points' && gameState?.maxPoints &&
      currentPlayerStats?.cumulativeScore !== undefined && currentPlayerStats?.cumulativeScore !== null &&
      (currentPlayerStats.cumulativeScore + dropPenalty >= gameState.maxPoints);
    // ?? DROP RULE: If player has drawn a card this turn, drop is disabled for this turn.
    // Drop is only available before drawing (first drop = 25pts, middle = 40pts).
    // It reactivates on the next turn if the player hasn't drawn yet.
    // Middle drop: once a card is drawn (deck OR discard pile) the Drop button stays visible but
    // INACTIVE until the player's next turn (server enforces the same rule).
    const hasDrawnNow = hasDrawnThisTurn || currentPlayerStats?.hasDrawnThisTurn === true;
    const canDrop = isPlayerTurn && !hasDrawnNow && !wouldExceedMaxPoints;
    // Drop is only blocked because it would eliminate the player: keep the button clickable to explain why
    const blockedOnly = isPlayerTurn && !hasDrawnNow && !!wouldExceedMaxPoints;
    const canGroup = atLeastTwoSelected;

    return (
                        <div className="action-buttons-panel inline-panel" style={{
                position: 'absolute', bottom: isLandscapeMode ? 'auto' : (isMobile ? '180px' : '185px'), top: isLandscapeMode ? '40px' : 'auto', right: '20px', display: 'flex', flexDirection: 'column',
                                        gap: '5px', padding: '8px', background: 'rgba(0, 0, 0, 0.7)', borderRadius: '8px', border: '1px solid #4CAF50',
        zIndex: 1100, minWidth: '100px'
      }}>
                {!isArrangeUsed && (
                  <button data-tour="arrange" className="action-btn arrange-btn" onClick={() => onAutoArrange()} title="Auto Arrange Cards by Suit"
                    style={{ background: '#9C27B0', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: 'pointer', width: '100%' }}>
                    Arrange
                  </button>
                )}
                {canGroup && (
          <button className="action-btn group-btn" onClick={onCreateGroup} title={`Create Group (${selectedCardsForGroup?.length || 0} selected)`}
            style={{ background: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: 'pointer', width: '100%' }}>
            Group ({selectedCardsForGroup?.length})
          </button>
        )}
        {selectedCardsForGroup && selectedCardsForGroup.length > 0 && (
          <button className="action-btn clear-btn" onClick={onResetGroupSelection} title="Clear Selection"
            style={{ background: '#607D8B', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: 'pointer', width: '100%' }}>
            Clear
          </button>
        )}
                <button className="action-btn declare-btn" onClick={onDeclaration} title="Declare: Selected card is discarded, remaining cards validated as groups" disabled={!canDeclare}
          style={{ background: canDeclare ? '#2196F3' : '#666', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: canDeclare ? 'pointer' : 'not-allowed', width: '100%' }}>
          Declare
        </button>
        <button className="action-btn discard-btn" onClick={onDiscard} title={exactlyOneSelected ? 'Discard Selected Card' : 'Select a card to discard'} disabled={!canDiscard}
          style={{ background: canDiscard ? '#dc3545' : '#666', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: canDiscard ? 'pointer' : 'not-allowed', width: '100%' }}>
          Discard
        </button>
                                {isPlayerTurn && (
                  <button className="action-btn drop-btn" disabled={!canDrop && !blockedOnly} onClick={() => {
                    if (blockedOnly) { if (onDropBlocked) onDropBlocked(); return; }
                    if (!canDrop) return;
                    // Determine correct drop penalty: first drop (25) vs middle drop (40)
                    // Use server's hasDrawnAnyCard (per-deal) for accuracy, fall back to hasDrawnThisTurn (per-turn)
                    const currentPlayer = gameState?.players?.find(p => p.userId === currentUserId || p.id === currentUserId);
                    const hasDrawnAnyInDeal = currentPlayer?.hasDrawnAnyCard === true;
                    const isFirstDrop = !hasDrawnAnyInDeal;
                    const penalty = isFirstDrop ? firstDropPenalty : 40;
                    const label = isFirstDrop ? 'First Drop' : 'Middle Drop';
                    // Professional modal popup
                    const modal = document.createElement('div');
                    modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:99999;font-family:Arial,sans-serif;animation:fadeIn 0.2s ease';
                    modal.innerHTML = `
                      <div style="background:#1a1a2e;border:2px solid #6c757d;border-radius:16px;padding:28px 32px;max-width:400px;width:90%;box-shadow:0 20px 60px rgba(0,0,0,0.5);text-align:center;animation:slideUp 0.3s ease">
                        <div style="font-size:42px;margin-bottom:12px">🃏</div>
                        <h3 style="color:#fff;margin:0 0 8px 0;font-size:20px">Drop from Game?</h3>
                        <p style="color:#ccc;margin:0 0 16px 0;font-size:14px;line-height:1.5">
                          You will receive a <strong style="color:#ff6b6b">${penalty} point penalty</strong>
                          (<span style="color:#ffd700">${label}</span>).
                        </p>
                        <p style="color:#aaa;margin:0 0 20px 0;font-size:12px">Are you sure you want to drop?</p>
                        <div style="display:flex;gap:12px;justify-content:center">
                          <button id="drop-cancel" style="flex:1;padding:12px 16px;border:1px solid #555;border-radius:10px;background:transparent;color:#ccc;font-size:14px;cursor:pointer;font-weight:bold">Cancel</button>
                          <button id="drop-confirm" style="flex:1;padding:12px 16px;border:none;border-radius:10px;background:#dc3545;color:white;font-size:14px;cursor:pointer;font-weight:bold">Yes, Drop</button>
                        </div>
                      </div>
                    `;
                    document.body.appendChild(modal);
                    document.getElementById('drop-confirm').onclick = () => {
                      document.body.removeChild(modal);
                      onDrop();
                    };
                    document.getElementById('drop-cancel').onclick = () => {
                      document.body.removeChild(modal);
                    };
                    modal.onclick = (e) => { if (e.target === modal) document.body.removeChild(modal); };
                    }} title={(() => {
                    const cp = gameState?.players?.find(p => p.userId === currentUserId || p.id === currentUserId);
                    return cp?.hasDrawnAnyCard ? 'Middle Drop (40 points)' : `First Drop (${firstDropPenalty} points)`;
                  })()}
                    style={{ background: canDrop ? '#6c757d' : '#444', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 8px', fontSize: isMobile ? '9px' : '11px', cursor: canDrop ? 'pointer' : 'not-allowed', opacity: canDrop ? 1 : 0.5, width: '100%' }}>
                    {(() => {
                      const cp = gameState?.players?.find(p => p.userId === currentUserId || p.id === currentUserId);
                      return cp?.hasDrawnAnyCard ? 'Drop (40 pts)' : `Drop (${firstDropPenalty} pts)`;
                    })()}
                  </button>
                )}

      </div>
    );
  };

  const getCardDimensions = () => {
    // Larger mobile cards (pinch-zoom available natively)
    const base = isMobile ? { width: 64, height: 90 } : { width: 70, height: 98 };
    const card = { width: `${base.width}px`, height: `${base.height}px` };
    return { deckCard: card, jokerCard: card, discardCard: card };
  };

  const getTableDimensions = () => {
    if (isMobile) {
      return { background: { width: '100%', height: '100%' }, center: { width: '96%', height: '50%', gap: isLandscapeMode ? '46px' : '64px' } };
    }
    return { background: { width: '80%', height: '96%' }, center: { width: '78%', height: '55%', gap: '120px' } };
  };

    const requestFullscreenElement = (element) => {
    if (element.requestFullscreen) {
      return element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      return element.webkitRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      return element.mozRequestFullScreen();
    } else if (element.msRequestFullscreen) {
      return element.msRequestFullscreen();
    }
    return Promise.reject(new Error('Fullscreen API not supported'));
  };

  const toggleFullscreen = () => { console.log('fullscreen removed'); };
  
  // Deck reshuffle animation state
  const [isReshuffling, setIsReshuffling] = useState(false);
  const [showReshuffleToast, setShowReshuffleToast] = useState(false);
  const reshuffleTimeoutRef = useRef(null);
  const reshuffleToastTimeoutRef = useRef(null);

  // Watch for reshuffle events from discard
  useEffect(() => {
    if (gameState?.wasReshuffled) {
      setIsReshuffling(true);
      setShowReshuffleToast(true);
      if (reshuffleTimeoutRef.current) clearTimeout(reshuffleTimeoutRef.current);
      if (reshuffleToastTimeoutRef.current) clearTimeout(reshuffleToastTimeoutRef.current);
      reshuffleTimeoutRef.current = setTimeout(() => {
        setIsReshuffling(false);
      }, 1000);
      reshuffleToastTimeoutRef.current = setTimeout(() => {
        setShowReshuffleToast(false);
      }, 2500);
    }
    return () => {
      if (reshuffleTimeoutRef.current) clearTimeout(reshuffleTimeoutRef.current);
      if (reshuffleToastTimeoutRef.current) clearTimeout(reshuffleToastTimeoutRef.current);
    };
  }, [gameState?.wasReshuffled]);

  const handleDeckClick = (e) => {
    e.stopPropagation();
    if (disabled || seatingPhase) return;
    if (!gameState?.stateHash) return;
    if (gameState.status !== 'playing') return;
    onDrawFromDeck();
  };

  const handleDiscardClick = (e) => {
    e.stopPropagation();
    if (disabled || seatingPhase) return;
    if (!gameState?.stateHash) return;
    if (gameState.status !== 'playing') return;
    onDrawFromDiscard();
  };

  const cardDimensions = getCardDimensions();
  const tableDimensions = getTableDimensions();

  return (
    <div className="game-table-container" style={{ width: '100%', height: '100%', position: 'relative', margin: '0 auto', overflowY: isMobile ? 'hidden' : 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y pinch-zoom', background: 'transparent', minHeight: '100%', boxSizing: 'border-box' }}>
      <div className="table-background-container" style={{ width: tableDimensions.background.width, height: tableDimensions.background.height, margin: 'auto', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <img src="/images/game-table.png" alt="Rummy Game Table" className="table-background-image" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', minWidth: '100%', minHeight: '100%' }}
          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.table-fallback-bg').style.display = 'block'; }} />
        <div className="table-fallback-bg" style={{ display: 'none', width: '100%', height: '100%', background: 'radial-gradient(circle, #1a472a, #0a1a0a)' }}></div>
      </div>

      {renderSeatingPhaseOverlay()}
      {renderWaitingHUD()}
      {renderDealingLayer()}

      <div className="players-container" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        {renderPlayerCircles()}
        {renderEmptyCircles()}
      </div>

      {!seatingPhase && !isWaiting && gameState?.status !== 'waiting' && (
        <div className="table-center" style={{ position: 'absolute', top: isLandscapeMode ? '42%' : (isMobile ? '41%' : '50%'), left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', justifyContent: 'center', alignItems: 'center', width: tableDimensions.center.width, height: tableDimensions.center.height, gap: tableDimensions.center.gap, zIndex: 50, paddingBottom: isLandscapeMode ? '60px' : '0px' }}>
          <div data-tour="draw" className="deck-area" onClick={handleDeckClick} ref={deckRef} style={{ position: 'relative', zIndex: 10 }}>
            {/* Deck + Joker stacked vertically */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? '12px' : '14px' }}>
              <div className="deck-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 5 }}>
                {gameState?.deckCount > 0 ? (
                  <div className={isReshuffling ? 'deck-card reshuffling' : 'deck-card'} style={{ width: cardDimensions.deckCard.width, height: cardDimensions.deckCard.height, borderRadius: '8px', background: 'linear-gradient(135deg, #1e3b70, #2a5298)', border: '3px solid #ffd700', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(0,0,0,0.6)', margin: '0', overflow: 'hidden', userSelect: 'none', WebkitUserSelect: 'none', msUserSelect: 'none', MozUserSelect: 'none', WebkitTouchCallout: 'none' }}>
                    {isReshuffling && (
                      <>
                        <div className="shuffle-particles" style={{ top: '30%', left: '20%', animationDelay: '0s' }} />
                        <div className="shuffle-particles" style={{ top: '60%', left: '70%', animationDelay: '0.1s' }} />
                        <div className="shuffle-particles" style={{ top: '20%', left: '60%', animationDelay: '0.2s' }} />
                        <div className="shuffle-particles" style={{ top: '70%', left: '30%', animationDelay: '0.15s' }} />
                        <div className="shuffle-particles" style={{ top: '45%', left: '85%', animationDelay: '0.25s' }} />
                        <div className="shuffle-particles" style={{ top: '55%', left: '10%', animationDelay: '0.05s' }} />
                      </>
                    )}
                    <div className="deck-card-back" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                      <div className="jv-rummy-text">
                        <div className="jv-text" style={{ fontSize: isMobile ? '16px' : '20px' }}>JV</div>
                        <div className="rummy-text" style={{ fontSize: isMobile ? '9px' : '12px' }}>Rummy</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="empty-deck" style={{ width: cardDimensions.deckCard.width, height: cardDimensions.deckCard.height, border: '2px dashed #666', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '12px', background: 'rgba(0,0,0,0.3)' }}>
                    empty
                  </div>
                )}
              </div>
                            <div style={{ color: '#ffd700', fontSize: isMobile ? '10px' : '12px', fontWeight: 'bold', textAlign: 'center', marginTop: '2px', letterSpacing: '1px', pointerEvents: 'none' }}>DRAW</div>
                            {/* Wild Joker: shown below deck */}
                            {jokerCard && (
                              <div className="joker-card-container" style={{ 
  position: 'absolute',
  top: '65%',                   // vertical position relative to deck-area
  left: '20%',                  // horizontal anchor
  transform: 'translate(-30%, -50%) rotate(-90deg)',  // shifts left & rotates
  zIndex: 3,                    // lower than deck-stack (zIndex:5)
  margin: '0',
  pointerEvents: 'none',
  opacity: 0.85
                              }}>
                                <div data-tour="joker" className="joker-card" style={{ 
                                  width: cardDimensions.jokerCard.width, 
                                  height: cardDimensions.jokerCard.height, 
                                  borderRadius: '8px', 
                                  boxShadow: '0 4px 15px rgba(0,0,0,0.5)', 
                                  border: '2px solid #ffd700', 
                                  background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)', 
                                  overflow: 'hidden', 
                                  position: 'relative', 
                                  animation: 'jokerGlow 2s infinite alternate' 
                                }}>
                                  <img 
                                    src={CardUtils.getCardImage ? CardUtils.getCardImage(jokerCard) : '/images/cards/back.png'} 
                                    alt={`Joker: ${jokerCard?.rank || 'Unknown'} of ${jokerCard?.suit || 'Unknown'}`} 
                                    className="joker-card-image" draggable={false} 
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '5px', padding: '0px', imageOrientation: 'none', transform: 'none' }} 
                                    onError={(e) => { e.target.src = '/images/cards/back.png'; }} />
                                </div>
                                <div className="joker-label" style={{ 
                                  color: '#ffd700', fontSize: isMobile ? '8px' : '10px', fontWeight: 'bold', textAlign: 'center', 
                                  padding: '2px 4px', marginTop: '4px',
                                  background: 'rgba(0,0,0,0.7)', borderRadius: '4px',
                                  whiteSpace: 'nowrap'
                                }}>
                                  Wild: {jokerCard.isJoker ? 'All 2s' : `${jokerCard.rank}s`}
                                </div>
                              </div>
                            )}
            </div>
          </div>

                    {/* 🃏 DECLARED DISCARD CARD - Shown during/after a declaration */}
          {gameState?.declaredDiscardCard && (() => {
            const dc = gameState.declaredDiscardCard;
            // releasing = wrong declaration: the card flips face up, then slides onto the discard pile
            const releasing = dc.releasing === true;
            const face = {
              position: 'absolute', inset: 0, borderRadius: '8px', overflow: 'hidden',
              backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            };
            return (
              <div className="declared-discard-container" style={{ margin: '0', position: 'relative' }}>
                <div
                  className={`declared-discard-card${releasing ? ' declared-release' : ''}`}
                  style={{
                    position: 'relative', width: cardDimensions.discardCard.width, height: cardDimensions.discardCard.height,
                    margin: '0', transformStyle: 'preserve-3d', WebkitTransformStyle: 'preserve-3d',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.7)', borderRadius: '8px',
                    transform: (dc.faceDown || releasing) ? undefined : 'rotateY(180deg)'
                  }}
                >
                  <div style={{ ...face, border: '3px dashed #FFC107', background: 'linear-gradient(135deg, #5a3e00, #8a6a00)', color: '#FFD700', fontSize: isMobile ? '11px' : '14px', fontWeight: 'bold', textAlign: 'center' }}>
                    🔒 Declared
                  </div>
                  <div style={{ ...face, border: '3px solid #4CAF50', background: 'white', transform: 'rotateY(180deg)' }}>
                    <img src={CardUtils.getCardImage(dc)} alt="Declared discard" draggable={false}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '5px', padding: '5px', imageOrientation: 'none' }}
                      onError={(e) => { e.target.src = '/images/cards/back.png'; }} />
                  </div>
                </div>
                <div style={{ textAlign: 'center', color: dc.faceDown ? '#FFC107' : '#4CAF50', fontSize: isMobile ? '7px' : '9px', marginTop: '4px', fontWeight: 'bold' }}>
                  {releasing ? 'Invalid - card released' : (dc.faceDown ? 'Declared (Hidden)' : 'Declared Card')}
                </div>
              </div>
            );
          })()}

                    <div data-tour="discard" className="discard-area" ref={discardRef} style={{ position: 'relative' }}>
            <div className={`discard-stack${isReshuffling ? ' reshuffling-to-deck' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {gameState?.discardPile?.length > 0 ? (
                <>
                  <div className="discard-card" onClick={handleDiscardClick} style={{ width: cardDimensions.discardCard.width, height: cardDimensions.discardCard.height, borderRadius: '8px', background: 'white', border: '3px solid #444', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s ease', margin: '0', overflow: 'hidden', userSelect: 'none', WebkitUserSelect: 'none', msUserSelect: 'none', MozUserSelect: 'none', WebkitTouchCallout: 'none' }}>
                    <img src={CardUtils.getCardImage(gameState.discardPile[gameState.discardPile.length - 1])} alt="Top discard card" className="discard-card-image" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '5px', padding: '5px', imageOrientation: 'none', transform: 'none' }} />
                  </div>
                  <div style={{ color: '#ffd700', fontSize: isMobile ? '10px' : '12px', fontWeight: 'bold', textAlign: 'center', marginTop: '4px', letterSpacing: '1px', pointerEvents: 'none' }}>DRAW</div>
                  <button 
                    className="discard-view-btn" 
                    onClick={(e) => { e.stopPropagation(); setShowAllDiscards(!showAllDiscards); }} 
                    title={showAllDiscards ? 'Hide discarded cards' : 'View all discarded cards'} 
                    style={{ background: showAllDiscards ? '#dc3545' : '#2196F3', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: isMobile ? '9px' : '10px', cursor: 'pointer', marginTop: '5px', transition: 'background 0.3s ease', zIndex: 200 }}
                  >
                    {showAllDiscards ? 'Hide All' : 'View All'}
                  </button>
                  {/* Discard viewer panel - shows when toggled */}
                  {showAllDiscards && gameState?.discardPile?.length > 0 && createPortal(
                    <div
                      className="discard-viewer-backdrop"
                      onClick={() => setShowAllDiscards(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}
                    >
                    <div 
                      className="discard-viewer-panel"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        // Rendered in a body-level portal with a very high z-index so it always
                        // appears ABOVE the player's hand (and the HUD), never underneath it.
                        position: 'relative',
                        background: 'rgba(0, 0, 0, 0.95)',
                        border: '2px solid #ffd700',
                        borderRadius: '12px',
                        padding: '12px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        justifyContent: 'center',
                        maxWidth: isMobile ? '90vw' : '420px',
                        minWidth: '200px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.8)',
                        maxHeight: isMobile ? '80vh' : '70vh',
                        overflowY: 'auto'
                      }}
                    >
                      {[...gameState.discardPile].reverse().map((card, idx) => (
                        <div key={idx} style={{
                          width: isMobile ? '36px' : '48px',
                          height: isMobile ? '50px' : '68px',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          border: '1px solid #444',
                          background: 'white',
                          flexShrink: 0
                        }}>
                          <img 
                            src={CardUtils.getCardImage(card)} 
                            alt={`Discarded ${card.rank} of ${card.suit}`}
                            draggable={false}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                          />
                        </div>
                      ))}
                    </div>
                    </div>,
                    document.body

                  )}
                </>
              ) : (
                <div className="empty-discard" style={{ width: cardDimensions.discardCard.width, height: cardDimensions.discardCard.height, border: '2px dashed #666', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '12px', background: 'rgba(0,0,0,0.3)' }}>
                  empty
                </div>
              )}
            </div>
          </div>

          {/* ♻️ Reshuffle Toast Notification */}
          {showReshuffleToast && (
            <div className="reshuffle-toast" style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0, 0, 0, 0.9)',
              color: '#FFD700',
              border: '2px solid #FFD700',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 'bold',
              zIndex: 1000,
              animation: 'reshuffleToastIn 0.3s ease-out, reshuffleToastOut 0.5s ease-in 2s forwards',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.4)',
              pointerEvents: 'none',
              userSelect: 'none'
            }}>
              ♻️ Deck Reshuffled!
              <div style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: 'normal', color: '#ccc', marginTop: '4px' }}>
                Discard pile shuffled into deck
              </div>
            </div>
          )}

                            </div>
      )}
      {renderCurrentPlayerHand()}

            {/* Declaration notification banner - TRANSPARENT so card arrangement is visible */}
      {declarationNotification && (
        <div className="declaration-notification-banner" style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'rgba(76, 175, 80, 0.2)',
          backdropFilter: 'blur(2px)',
          color: '#ffffff', padding: '16px 32px', borderRadius: '12px', zIndex: 1002,
          fontSize: isMobile ? '14px' : '18px', fontWeight: 'bold',
          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
          textAlign: 'center',
          border: '1px solid rgba(76, 175, 80, 0.4)',
          maxWidth: '80%',
          pointerEvents: 'none',
          animation: 'pulse-notification 1.5s ease-in-out infinite',
        }}>
          {declarationNotification}
        </div>
      )}

      {!seatingPhase && renderActionButtons()}
    </div>
  );
};

export default GameTable;
